import React from 'react'

import { Divider, InputAdornment, InputProps as InputPropsType } from '@material-ui/core'

import { makeAppStyles } from '../../themes'

import { DateInput, DateInputProps } from './DateInputs'

import { Today } from '../../icons'

import { isAfter, isBefore } from 'date-fns'

import { getTimeAgnosticDate } from '@percept/utils'

import { DateType } from '@percept/types'


const useDateRangeStyles = makeAppStyles( theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  picker: {
    marginRight: theme.spacing(2),
  },
  icon: {
    fontSize: '1.2em',
  },
}))


export type DateRangeValue = [DateType, DateType]


export type DateRangeInputProps = (
  Omit<
    DateInputProps, 'value' | 'onChange'
  > & {
    value: DateRangeValue
    domain?: [DateType | null, DateType | null]
    onChange: (dates: [number, number]) => void
    divided?: boolean
  }
)

// We epoch shift the date so that the picker shows the expected values
const dateToNumberEpochShift = (d: DateType): number => (
  getTimeAgnosticDate(d).getTime()
)


export const DateRangeInput = ({
  value,
  domain,
  onChange,
  InputProps = {},
  label,
  color = 'primary',
  divided = false,
  minDate,
  maxDate,
  ...props
}: DateRangeInputProps): JSX.Element => {

  const [start, end] = value.map(dateToNumberEpochShift)

  const [sourceMin, sourceMax] = domain || [null, null]

  let min = null
  if( minDate ){
    min = dateToNumberEpochShift(new Date(minDate as Date | string | number))
  }else if( sourceMin ){
    min = dateToNumberEpochShift(sourceMin)
  }

  let max = null
  if( maxDate ){
    max = dateToNumberEpochShift(new Date(maxDate as Date | string | number))
  }else if( sourceMax ){
    max = dateToNumberEpochShift(sourceMax)
  }

  const classes = useDateRangeStyles()

  const inputProps: Partial<InputPropsType> = {
    startAdornment: (
      <InputAdornment position='start'>
        <Today className={classes.icon} />
      </InputAdornment>
    ),
    ...InputProps,
  }

  return (
    <div className={classes.root}>

      <DateInput
        variant='inline'
        format='dd/MM/yy'
        className={classes.picker}
        minDate={min || undefined}
        maxDate={max || undefined}
        color={color}
        {...props}
        label={label}
        InputProps={inputProps}
        value={start}
        onChange={(newStart): void => {
          if( newStart ){
            const endDate = new Date(end)
            const newEnd = isAfter(newStart, endDate) ? newStart : endDate
            onChange([newStart.getTime(), newEnd.getTime()])
          }
        }} />

      { divided && <Divider flexItem orientation='vertical' /> }

      <DateInput
        variant='inline'
        format='dd/MM/yy'
        minDate={min || undefined}
        maxDate={max || undefined}
        color={color}
        {...props}
        // trigger an empty label holder for sizing consistency
        // if a valid label is supplied for the overall input
        label={label ? ' ' : undefined}
        InputProps={inputProps}
        value={end}
        onChange={(newEnd): void => {
          if( newEnd ){
            const startDate = new Date(start)
            const newStart = isBefore(newEnd, startDate) ? newEnd : startDate
            onChange([newStart.getTime(), newEnd.getTime()])
          }
        }} />

    </div>
  )
}
